// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase API client (Stage 6P.1 + 6P.2).
//
// Thin axios wrappers around:
//   /api/erp/purchase/requests/*  (Purchase Requests — 6P.1)
//   /api/erp/purchase/orders/*    (Purchase Orders   — 6P.2)
//
// Mirrors the glassCategoryApi shape — the `unwrap` helper
// handles both the {data:{data:...}} envelope used by most
// list/create endpoints and the bare {data:...} shape used by
// a handful of helper routes.
//
// Backend is the source of truth for all money math; these
// helpers do not transform payloads — they just pass them
// through and surface the unwrapped response body.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const PR_BASE = '/api/erp/purchase/requests';
const PO_BASE = '/api/erp/purchase/orders';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const purchaseRequestsApi = {
  list:        (params = {}) => client.get(PR_BASE, { params }).then(unwrap),
  get:         (id)          => client.get(`${PR_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create:      (payload)     => client.post(PR_BASE, payload).then(unwrap),
  update:      (id, payload) => client.put(`${PR_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove:      (id)          => client.delete(`${PR_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  submit:      (id)          => client.post(`${PR_BASE}/${encodeURIComponent(id)}/submit`).then(unwrap),
  approve:     (id)          => client.post(`${PR_BASE}/${encodeURIComponent(id)}/approve`).then(unwrap),
  reject:      (id, { reason } = {}) =>
    client.post(`${PR_BASE}/${encodeURIComponent(id)}/reject`, { reason }).then(unwrap),
  cancel:      (id, { reason } = {}) =>
    client.post(`${PR_BASE}/${encodeURIComponent(id)}/cancel`, { reason }).then(unwrap),
  convertToPo: (id, payload) =>
    client.post(`${PR_BASE}/${encodeURIComponent(id)}/convert-to-po`, payload).then(unwrap)
};

export const purchaseOrdersApi = {
  list:          (params = {}) => client.get(PO_BASE, { params }).then(unwrap),
  get:           (id)          => client.get(`${PO_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create:        (payload)     => client.post(PO_BASE, payload).then(unwrap),
  update:        (id, payload) => client.put(`${PO_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove:        (id)          => client.delete(`${PO_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  place:         (id)          => client.post(`${PO_BASE}/${encodeURIComponent(id)}/place`).then(unwrap),
  cancel:        (id, { reason } = {}) =>
    client.post(`${PO_BASE}/${encodeURIComponent(id)}/cancel`, { reason }).then(unwrap),
  close:         (id)          => client.post(`${PO_BASE}/${encodeURIComponent(id)}/close`).then(unwrap),
  previewTotals: (payload)     => client.post(`${PO_BASE}/preview-totals`, payload).then(unwrap)
};

export default { purchaseRequestsApi, purchaseOrdersApi };
